/**
 * Ответ API → снимок для страницы хранилища.
 *
 * Страница презентационная: она печатает готовые величины и не знает ни про
 * стадии сборки, ни про то, что сервер считает деньги посуточно. Оба перевода
 * живут здесь.
 *
 * @format
 */

import {AD_SYSTEM_LABEL, RISK_DAYS, type ReportEntry, type ReportsArchiveData} from 'ui';

import {ERROR_REASON_LABEL, type ReportErrorReason, type ReportStatus} from '../../content';
import {num, type Decimal} from '../../Report/helpers/toReportData';

interface RawProject {
  score?: number | null;
  risk?: Decimal;
  riskDailySpend?: Decimal;
}

export interface RawReport {
  id: string;
  periodStart: string;
  periodEnd: string;
  status: string;
  generatedAt?: string | null;
  errorReason?: string | null;
  reportData?: {
    meta?: {adSystem?: string | null} | null;
    project?: RawProject | null;
  } | null;
}

/**
 * Стадия сборки → состояние строки. Двум стадиям соответствует одно.
 *
 * Типизирована по `ReportStatus`, а не по `string`: пропущенная стадия —
 * ошибка компиляции, а не молчаливая подмена дефолтом.
 */
const STATUS: Record<ReportStatus, ReportEntry['status']> = {
  READY: 'ready',
  COLLECTING: 'building',
  GENERATING: 'building',
  PENDING: 'scheduled',
  ERROR: 'failed',
};

/**
 * Тот же словарь, но признающий, что стадия может оказаться незнакомой.
 *
 * `report.status` в `RawReport` — `string`: сервер расширяет перечисление
 * стадий раньше, чем фронт про это узнаёт (в этом же проекте так уже
 * случалось со слагаемыми балла). Индексация напрямую через `STATUS[status
 * as ReportStatus]` успокаивает компилятор, но на незнакомой строке даёт
 * `undefined` в рантайме — и роняет всю строку списка на `status.tone`.
 * Ослабленный тип индекс-сигнатуры нужен ещё и затем, чтобы
 * `@typescript-eslint/no-unnecessary-condition` не счёл `?? 'building'`
 * ниже лишним и не срезал его через `eslint --fix` при коммите.
 */
const KNOWN_STATUS: {readonly [key: string]: ReportEntry['status'] | undefined} = STATUS;

/**
 * Месячная величина из суточной.
 *
 * `risk` и `riskDailySpend` сервер отдаёт за сутки, а отчёт показывает их за
 * месяц. Множитель тот же, что на этаже «Деньги под риском», — иначе одна и
 * та же кампания стоит в списке в тридцать раз дешевле, чем в документе.
 */
function monthly(value: Decimal): number | undefined {
  const daily = num(value);
  return daily == null ? undefined : Math.round(daily * RISK_DAYS);
}

/** Момент сборки → календарная дата: страница печатает день, а не время. */
function eventDate(generatedAt?: string | null): string | undefined {
  return generatedAt ? generatedAt.slice(0, 10) : undefined;
}

/**
 * Свежие сверху. Порядок из API не документирован, а витрина берёт первый
 * готовый отчёт списка — задаём порядок сами.
 */
function newestFirst(a: RawReport, b: RawReport): number {
  if (a.periodStart !== b.periodStart) return a.periodStart < b.periodStart ? 1 : -1;
  return (b.generatedAt ?? '').localeCompare(a.generatedAt ?? '');
}

export function toArchiveData(
  companyName: string | null | undefined,
  reports: readonly RawReport[],
): ReportsArchiveData {
  const sorted = [...reports].sort(newestFirst);

  const entries = sorted.map((report, index): ReportEntry => {
    const project = report.reportData?.project;
    const score = project?.score ?? undefined;
    const reason = report.errorReason as ReportErrorReason | null | undefined;

    // Дельта сравнивает с предыдущим ГОТОВЫМ отчётом: между двумя готовыми
    // может стоять сорвавшийся, и балла у него нет.
    const previous = sorted
      .slice(index + 1)
      .find(older => older.reportData?.project?.score != null);

    return {
      id: report.id,
      periodFrom: report.periodStart,
      periodTo: report.periodEnd,
      // Незнакомая стадия вероятнее всего новая промежуточная (как
      // `COLLECTING`/`GENERATING`, а не терминальная `READY`/`ERROR`) —
      // «Собирается» самое честное, что можно сказать, не зная кода.
      status: KNOWN_STATUS[report.status] ?? 'building',
      date: eventDate(report.generatedAt),
      score,
      scorePrev: score != null ? (previous?.reportData?.project?.score ?? undefined) : undefined,
      atRisk: monthly(project?.risk),
      spend: monthly(project?.riskDailySpend),
      note: reason ? ERROR_REASON_LABEL[reason] : undefined,
    };
  });

  // Рекламная система — свойство проекта, но в схеме она лежит только внутри
  // снимка. Берём у первого готового отчёта; готовых нет — бейджа не будет.
  const adSystemCode = sorted.find(report => report.reportData?.meta?.adSystem)?.reportData?.meta
    ?.adSystem;

  return {
    companyName: companyName ?? undefined,
    adSystem: adSystemCode ? AD_SYSTEM_LABEL[adSystemCode] : undefined,
    entries,
  };
}
